import { Dialog, DialogContent } from "@/components/ui/dialog";
import { LuLoaderCircle } from "react-icons/lu";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  addBrand,
  searchBrands,
  fetchBrandCategories,
  fetchCategoryBrands,
} from "@/screens/analysis/apiService";
import { debounce } from "lodash";
import {
  AccountBrand,
  SearchBrandItem,
  BrandCategory,
  BrandDetail,
} from "@/screens/analysis/types";
import { toast } from "sonner";
import SearchBrandDropdown from "./search/SearchBrandDropdown";
import CategoryChips from "./CategoryChips";
import CategoryBrandsList from "./CategoryBrandsList";
import ConfirmationDialog from "./ConfirmationDialog";
import SelectedBrands from "./SelectedBrands";
import { ImportExternalBrandDialog } from "./ImportExternalBrandDialog";
import axios from "axios";
import { RiSearchLine } from "@remixicon/react";
import { SqButton } from "@/components/squircle-ui/button";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import ToastCompact from "@/components/common/toast/ToastCompact";

export function AddCompetitorDialog({
  accountId,
  isOpen,
  onOpenChange,
  onBrandAdded,
  onBrandAddedOptimistic,
  onBrandAddedSuccess,
  onBrandAddedError,
  addedBrands,
}: {
  accountId: string | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onBrandAdded?: (brand: AccountBrand) => void;
  onBrandAddedOptimistic?: (brand: AccountBrand) => void;
  onBrandAddedSuccess?: (optimisticBrandId: string, actualBrand: AccountBrand) => void;
  onBrandAddedError?: (optimisticBrandId: string) => void;
  addedBrands?: AccountBrand[];
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddingBrand, setIsAddingBrand] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<SearchBrandItem[]>([]);
  const [selectedCategoryBrands, setSelectedCategoryBrands] = useState<AccountBrand[]>(
    [],
  );
  const [searchResults, setSearchResults] = useState<SearchBrandItem[]>([]);
  const [isSearchingBrands, setIsSearchingBrands] = useState<boolean | null>(null);
  const [openDropdown, setOpenDropdown] = useState(true);
  const [categories, setCategories] = useState<BrandCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [isCategoryLoading, setIsCategoryLoading] = useState(true);
  const [categoryBrands, setCategoryBrands] = useState<AccountBrand[]>([]);
  const [isLoadingCategoryBrands, setIsLoadingCategoryBrands] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMoreBrands, setIsLoadingMoreBrands] = useState(false);
  const [showImportExternalDialog, setShowImportExternalDialog] = useState(false);
  const [privateBrands, setPrivateBrands] = useState<(AccountBrand | SearchBrandItem)[]>(
    [],
  );
  const cancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const allSelectedBrands = useMemo(
    () => [...selectedBrands, ...selectedCategoryBrands],
    [selectedBrands, selectedCategoryBrands],
  );

  const fetchCategoryBrandsData = useCallback(
    async (categoryIds: string[], pageNum: number = 1, append: boolean = false) => {
      try {
        if (append) {
          setIsLoadingMoreBrands(true);
        } else {
          setIsLoadingCategoryBrands(true);
        }

        const result = await fetchCategoryBrands({
          categoryIds,
          pageNum,
          pageSize: 40,
        });

        if (append) {
          setCategoryBrands((prev) => [...prev, ...result.brands]);
        } else {
          setCategoryBrands(result.brands);
        }

        setTotalCount(result.total_count);
        setHasNextPage(result.has_next);
        setCurrentPage(pageNum);
      } catch (error) {
        console.error("Failed to fetch category brands:", error);
        if (!append) {
          setCategoryBrands([]);
        }
      } finally {
        setIsLoadingCategoryBrands(false);
        setIsLoadingMoreBrands(false);
      }
    },
    [],
  );

  const fetchCategories = useCallback(async () => {
    try {
      setIsCategoryLoading(true);
      const result = await fetchBrandCategories({});
      setCategories(result);
      if (result.length > 0) {
        setSelectedCategoryId(result[0]?.key);
        void fetchCategoryBrandsData([result[0]?.key], 1, false);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setIsCategoryLoading(false);
    }
  }, [fetchCategoryBrandsData]);

  const handleBrandSearch = async (query: string) => {
    // Cancel the previous request if it exists
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    // Create a new cancel token for this request
    const cancelToken = axios.CancelToken.source();
    cancelTokenRef.current = cancelToken;
    try {
      setIsSearchingBrands(true);
      const result = await searchBrands({ query, cancelToken: cancelToken.token });
      setSearchResults(result.data);
      setIsSearchingBrands(false);
    } catch (error) {
      if (axios.isCancel(error)) {
        // Request was cancelled, do nothing
      } else {
        console.error("Failed to search brands:", error);
        setIsSearchingBrands(false);
      }
    }
  };

  const debouncedSearchQuery = useMemo(() => debounce(handleBrandSearch, 500), []);

  useEffect(() => {
    return () => {
      debouncedSearchQuery.cancel();
      // Cancel any ongoing request when component unmounts
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Component unmounted.");
      }
    };
  }, [debouncedSearchQuery]);

  // Fetch categories on component mount
  useEffect(() => {
    if (!isOpen) return;
    void fetchCategories();
  }, [isOpen, fetchCategories]);

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setCurrentPage(1); // Reset pagination
    setHasNextPage(false); // Reset pagination state
    void fetchCategoryBrandsData([categoryId], 1, false);
  };

  const handleCategoryBrandSelect = (brand: AccountBrand) => {
    setSelectedCategoryBrands((prev) => {
      const isSelected = prev.some((b) => b.id === brand.id);
      if (isSelected) {
        // Remove if already selected
        return prev.filter((b) => b.id !== brand.id);
      } else {
        // Check if adding this brand would exceed the limit
        const totalSelected = selectedBrands.length + prev.length;
        if (totalSelected >= 5) {
          toast.custom((t) => (
            <ToastCompact
              isWarning={true}
              messagePrefix="Maximum brands added"
              descriptionPrefix="You can manage up to 5 brands at once."
              hasDescription={true}
              onDismiss={() => toast.dismiss(t)}
            />
          ));
          return prev;
        }
        // Add if not selected
        return [...prev, brand];
      }
    });
  };

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setSearchQuery(url);
    const trimmedUrl = url.trim();
    if (trimmedUrl) {
      void debouncedSearchQuery(trimmedUrl);
    } else {
      setSearchResults([]);
      setIsSearchingBrands(null);
    }
  };

  const createOptimisticBrand = (
    searchBrand: SearchBrandItem | AccountBrand,
  ): AccountBrand => {
    // Generate a temporary ID for the optimistic brand
    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    if ("profile_picture_url" in searchBrand) {
      return {
        id: optimisticId,
        page_id: searchBrand.page_id,
        ad_library_url: searchBrand.ad_library_url || "",
        name: searchBrand.page_name,
        url: searchBrand.page_url,
        profile_image_url: searchBrand.profile_picture_url,
        ad_count: 0,
        sync_status: "pending",
        last_synced_at: new Date().toISOString(),
      };
    } else {
      return {
        id: optimisticId,
        page_id: searchBrand.page_id,
        ad_library_url: searchBrand.ad_library_url || "",
        name: searchBrand.name,
        url: searchBrand.url,
        profile_image_url: searchBrand.profile_image_url,
        ad_count: 0,
        sync_status: "pending",
        last_synced_at: new Date().toISOString(),
      };
    }
  };

  const handleAddBrand = async () => {
    if (!accountId) return;

    const allSelectedBrands = [...selectedBrands, ...selectedCategoryBrands];
    if (allSelectedBrands.length === 0) return;

    // Create brand details for API
    const brandDetails: BrandDetail[] = allSelectedBrands.map((brand) => {
      if ("profile_picture_url" in brand) {
        // SearchBrandItem
        return {
          page_url: brand.page_url,
          brand_platform: "FACEBOOK",
          account_id: accountId,
          page_id: brand.page_id,
          page_name: brand.page_name,
          profile_picture_url: brand.profile_picture_url,
          ad_library_url: brand.ad_library_url,
          category: brand.category || undefined,
        };
      } else {
        // AccountBrand
        return {
          page_url: brand.url,
          ad_library_url: brand.ad_library_url,
          brand_platform: "FACEBOOK",
          account_id: accountId,
          page_id: brand.page_id,
          page_name: brand.name,
          profile_picture_url: brand.profile_image_url || undefined,
        };
      }
    });

    // Create optimistic brands
    const optimisticBrands: AccountBrand[] = allSelectedBrands.map((brand) => {
      return createOptimisticBrand(brand);
    });

    // Use optimistic updates if callbacks are provided
    if (onBrandAddedOptimistic && onBrandAddedSuccess && onBrandAddedError) {
      optimisticBrands.forEach((optimisticBrand) => {
        onBrandAddedOptimistic(optimisticBrand);
      });
    }

    setIsAddingBrand(true);
    handleDialogChange(false, true);
    try {
      const response = await addBrand({
        accountId,
        brandDetails,
      });

      // Handle the bulk response
      const successfulBrands: AccountBrand[] = [];
      const failedBrandIds: string[] = [];
      const privateBrandsList: (AccountBrand | SearchBrandItem)[] = [];

      response.results.forEach((result, index) => {
        if (result.status === "success" && result.brand) {
          // Regular successful brand
          successfulBrands.push(result.brand);
          if (onBrandAddedSuccess) {
            onBrandAddedSuccess(optimisticBrands[index].id, result.brand);
          }
          if (onBrandAdded) {
            onBrandAdded(result.brand);
          }
        } else if (result.status === "error" && result.brand && result.brand.is_private) {
          // Private brand error - brand exists but cannot be added without add_private_brand flag
          const originalBrand = allSelectedBrands[index];
          if (originalBrand) {
            privateBrandsList.push(originalBrand);
          }
          // Remove optimistic update for now - will be re-added when import succeeds
          if (onBrandAddedError) {
            onBrandAddedError(optimisticBrands[index].id);
          }
        } else {
          // Other error cases
          failedBrandIds.push(optimisticBrands[index].id);
          if (onBrandAddedError) {
            onBrandAddedError(optimisticBrands[index].id);
          }
        }
      });

      // Show import external dialog if there are private brands
      if (privateBrandsList.length > 0) {
        setPrivateBrands(privateBrandsList);
        setShowImportExternalDialog(true);
      }
    } catch (error) {
      // Remove all optimistic brands on error
      if (onBrandAddedError) {
        optimisticBrands.forEach((optimisticBrand) => {
          onBrandAddedError(optimisticBrand.id);
        });
      }

      // Check if error response contains private brands
      if (
        axios.isAxiosError<{ results?: Array<{ status: string; brand?: AccountBrand }> }>(
          error,
        )
      ) {
        const errorResults = error.response?.data?.results;
        if (!errorResults) {
          console.error("Failed to add brands:", error);
          return;
        }
        const privateBrandsList: (AccountBrand | SearchBrandItem)[] = [];

        errorResults.forEach((result, index) => {
          if (result.status === "error" && result.brand?.is_private) {
            const originalBrand = allSelectedBrands[index];
            if (originalBrand) {
              privateBrandsList.push(originalBrand);
            }
          }
        });

        // Show import external dialog if there are private brands
        if (privateBrandsList.length > 0) {
          setPrivateBrands(privateBrandsList);
          setShowImportExternalDialog(true);
        } else {
          // No private brands found, show generic error
          console.error("Failed to add brands:", error);
        }
      } else {
        // Not an axios error or no response data
        console.error("Failed to add brands:", error);
      }
    } finally {
      setIsAddingBrand(false);
    }
  };

  const handleDialogChange = (open: boolean, disableConfirmationDialog = false) => {
    if (!open) {
      // Check if there are selected brands that haven't been added
      if (allSelectedBrands.length > 0 && !disableConfirmationDialog) {
        setShowConfirmationDialog(true);
        return; // Don't close the dialog yet
      }

      // Reset all states when dialog closes
      resetDialogState();
    }
    onOpenChange(open);
  };

  const handleLoadMoreBrands = useCallback(() => {
    if (selectedCategoryId && hasNextPage && !isLoadingMoreBrands) {
      void fetchCategoryBrandsData([selectedCategoryId], currentPage + 1, true);
    }
  }, [
    selectedCategoryId,
    hasNextPage,
    isLoadingMoreBrands,
    currentPage,
    fetchCategoryBrandsData,
  ]);

  const resetDialogState = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSelectedBrands([]);
    setCategories([]);
    setSelectedCategoryBrands([]);
    setIsSearchingBrands(null);
    setSelectedCategoryId(null);
    setCategoryBrands([]);
    setShowConfirmationDialog(false);
    setShowImportExternalDialog(false);
    setPrivateBrands([]);
    setCurrentPage(1);
    setHasNextPage(false);
    setIsLoadingMoreBrands(false);
  };

  const handleImportFromAdLibrary = (addedBrands: AccountBrand[]) => {
    // Close the dialog
    setShowImportExternalDialog(false);

    // Add imported brands directly to the brands list
    // Since these are real brands (not optimistic), we use onBrandAddedOptimistic
    // to add them to the list - they have real IDs so loadAds will work normally
    addedBrands.forEach((brand) => {
      if (onBrandAddedOptimistic) {
        onBrandAddedOptimistic(brand);
      }
    });

    setPrivateBrands([]);
  };

  const handleCancelImport = () => {
    // Private brands were already removed from optimistic updates in handleAddBrand
    // Just close the dialog
    setShowImportExternalDialog(false);
    setPrivateBrands([]);
  };

  const handleConfirmClose = () => {
    resetDialogState();
    onOpenChange(false);
  };

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
  };

  const handleRemoveBrand = (brandToRemove: AccountBrand | SearchBrandItem) => {
    if ("profile_picture_url" in brandToRemove) {
      // Remove from search brands
      setSelectedBrands((prev) =>
        prev.filter((b) => b.page_id !== brandToRemove.page_id),
      );
    } else {
      // Remove from category brands
      setSelectedCategoryBrands((prev) => prev.filter((b) => b.id !== brandToRemove.id));
    }
  };

  const handleAddAndContinue = async () => {
    await handleAddBrand();
    // Dialog will be closed by handleAddBrand, so no need to close it here
  };

  useEffect(() => {
    if (!openDropdown) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        inputContainerRef.current &&
        !inputContainerRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogChange}>
        <DialogContent
          hideCloseButton
          className="p-[6px] w-full h-full max-h-[654px] lg:max-w-[828px] flex flex-col gap-4 rounded-[12px] squircle-mask squircle-radius-[12px] squircle-smooth-[1]"
        >
          <TooltipProvider>
            <div className="flex items-center justify-between">
              <div
                ref={inputContainerRef}
                className="relative z-10 flex items-center w-full max-w-[522px] min-h-[32px] px-[9px] py-[6px] rounded-[8px] squircle squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white gap-[8px]"
              >
                <div className="flex items-center justify-center flex-shrink-0 text-[#98A2B3]">
                  <RiSearchLine size={16} className="text-[#617390]" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  onFocus={() => setOpenDropdown(true)}
                  placeholder="Search Brand or Paste Facebook Page URL"
                  className="w-full text-[12px] text-brand-primary-800 outline-none border-none bg-transparent placeholder:text-brand-primary-400 font-normal leading-[20px]"
                />
                {searchQuery && (
                  <div
                    className={`absolute z-10 -bottom-2 left-0 translate-y-full w-[120%] ${
                      openDropdown ? "block" : "hidden"
                    }`}
                  >
                    <SearchBrandDropdown
                      brands={searchResults}
                      addedBrands={addedBrands}
                      loading={isSearchingBrands}
                      searchQuery={searchQuery}
                      selectedBrands={selectedBrands}
                      onSelect={(brand) => {
                        setSelectedBrands((prev) => {
                          const isSelected = prev.some(
                            (b) => b.page_id === brand.page_id,
                          );
                          if (isSelected) {
                            return prev.filter((b) => b.page_id !== brand.page_id);
                          } else {
                            // Check if adding this brand would exceed the limit
                            const totalSelected =
                              prev.length + selectedCategoryBrands.length;
                            if (totalSelected >= 5) {
                              toast.error("You can only select up to 5 brands at a time");
                              return prev;
                            }
                            return [...prev, brand];
                          }
                        });
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-[6px]">
                {allSelectedBrands.length > 0 && (
                  <SelectedBrands
                    allSelectedBrands={allSelectedBrands}
                    onRemoveBrand={handleRemoveBrand}
                  />
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SqButton
                      variant="default"
                      size={"md"}
                      onClick={() => void handleAddBrand()}
                      disabled={
                        (selectedBrands.length === 0 &&
                          selectedCategoryBrands.length === 0) ||
                        isAddingBrand
                      }
                      className="min-w-[48px]"
                    >
                      {isAddingBrand ? (
                        <LuLoaderCircle className="animate-spin" size={16} />
                      ) : (
                        <span className="font-medium">Add</span>
                      )}
                    </SqButton>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="end">
                    <p>Add Selected Brands</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            {isCategoryLoading ? (
              <div className="h-full w-full flex justify-center items-center">
                <WaveLoader />
              </div>
            ) : (
              <div className="flex flex-col gap-4 flex-1 overflow-hidden">
                {/* Category Selection */}
                <div className="flex flex-col gap-2">
                  <CategoryChips
                    categories={categories}
                    selectedCategoryId={selectedCategoryId}
                    onSelect={handleCategorySelect}
                  />
                </div>

                {/* Category Brands List */}
                {selectedCategoryId && (
                  <div className="flex-1 overflow-hidden">
                    <CategoryBrandsList
                      brands={categoryBrands}
                      addedBrands={addedBrands}
                      totalCount={totalCount}
                      categoryName={
                        categories.find((c) => c.key === selectedCategoryId)?.label
                      }
                      isLoading={isLoadingCategoryBrands}
                      onSelectBrand={handleCategoryBrandSelect}
                      selectedBrands={selectedCategoryBrands}
                      hasNext={hasNextPage}
                      onLoadMore={handleLoadMoreBrands}
                      isLoadingMore={isLoadingMoreBrands}
                    />
                  </div>
                )}
              </div>
            )}
          </TooltipProvider>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmationDialog}
        onOpenChange={setShowConfirmationDialog}
        onStay={handleCancelClose}
        onDiscard={handleConfirmClose}
        onAddAndContinue={() => void handleAddAndContinue()}
        isAdding={isAddingBrand}
        message={
          <p className="text-[13px] text-brand-primary-600">
            You've selected brands that aren't added yet. Select{" "}
            <span className="font-medium text-brand-primary-800">"Add & continue"</span>{" "}
            to save your changes, or{" "}
            <span className="font-medium text-brand-primary-800">"Discard"</span> to exit
            without Adding.
          </p>
        }
      />

      {/* Import External Brand Dialog */}
      <ImportExternalBrandDialog
        isOpen={showImportExternalDialog}
        onOpenChange={setShowImportExternalDialog}
        privateBrands={privateBrands}
        onImport={handleImportFromAdLibrary}
        onCancel={handleCancelImport}
        accountId={accountId}
      />
    </>
  );
}
